import { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useTranslation } from "react-i18next";
import { Sheet } from "react-modal-sheet";
import IconClose from "@/assets/icons/close.svg?react";
import ChevronIcon from "@/assets/icons/chevron.svg?react";
import VibratingButton from "@/components/base/button/VibratingButton";
import BaseButton from "@/components/base/button/BaseButton";
import { useDistricts, useRegions } from "./edit-profile.api";

interface Props {
  isLoading?: boolean;
}

const RegionDistrictSelect = ({ isLoading }: Props) => {
  const { control, setValue, watch } = useFormContext();
  const [error, setError] = useState<null | string>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState("region");
  const [tempDistrict, setTempDistrict] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedRegion = watch("region_id");
  const selectedDistrict = watch("district_id");
  const { t } = useTranslation();

  const {
    data: regions = [],
    isLoading: isLoadingRegions,
    error: regionsError,
  } = useRegions();

  const {
    data: districts = [],
    isLoading: isLoadingDistricts,
    error: districtsError,
  } = useDistricts(selectedRegion);

  useEffect(() => {
    // For edit case - set temp district when opening sheet if a district is already selected
    if (isSheetOpen && selectionMode === "district" && selectedDistrict) {
      setTempDistrict(selectedDistrict);
    }
  }, [isSheetOpen, selectionMode, selectedDistrict]);

  useEffect(() => {
    if (regionsError && !error) {
      setError(t("region_fetch_error"));
    }
    if (districtsError && !error) {
      setError(t("district_fetch_error"));
    }
  }, [regionsError, districtsError, error, t]);

  const selectedRegionName =
    regions.find((r) => r.id === selectedRegion)?.name || "";
  const selectedDistrictName =
    districts.find((d) => d.id === selectedDistrict)?.name || "";
  const displayValue =
    selectedRegionName && selectedDistrictName
      ? `${selectedRegionName}, ${selectedDistrictName}`
      : selectedRegionName || t("select_region_district");

  const handleSelectRegion = (regionId: string) => {
    setValue("region_id", regionId, {
      shouldValidate: true,
      shouldDirty: true,
    });

    if (regionId !== selectedRegion) {
      setValue("district_id", "", { shouldValidate: true, shouldDirty: true });
      setTempDistrict("");
    } else if (selectedDistrict) {
      setTempDistrict(selectedDistrict);
    }

    setSelectionMode("district");
  };

  const handleSelectDistrict = (districtId: string) => {
    setTempDistrict(districtId);
  };

  const handleConfirmDistrict = async () => {
    setIsSubmitting(true);
    try {
      if (tempDistrict) {
        setValue("district_id", tempDistrict, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      setIsSheetOpen(false);
      setSelectionMode("region"); // Reset for next opening
      setError(null);
    } catch (err) {
      setError((err as Error)?.message || t("submit_error"));
      console.error("Error saving:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openSheet = () => {
    setIsSheetOpen(true);
    // Start from region selection if no region is selected yet
    // Otherwise go straight to district selection for better edit experience
    setSelectionMode(selectedRegion ? "district" : "region");

    // For edit case - initialize tempDistrict with current value
    if (selectedDistrict) {
      setTempDistrict(selectedDistrict);
    } else {
      setTempDistrict("");
    }
  };

  const closeSheet = () => {
    setIsSheetOpen(false);
    setSelectionMode("region");
  };

  if (isLoading) {
    return (
      <div className="overflow-hidden space-y-3">
        <div className="skeleton w-1/3 h-[20px] rounded-sm" />
        <div className="skeleton w-full h-11 rounded-10" />
      </div>
    );
  }

  return (
    <FormField
      control={control}
      name="region_id"
      render={() => (
        <FormItem>
          <FormLabel>{t("select_region")}</FormLabel>
          <FormControl>
            <div className="relative w-full max-w-full !px-0 overflow-x-hidden">
              <div
                className="bg-gray-4 text-white p-2.5 rounded-10 border-b border-white/08 focus:outline-none cursor-pointer flex justify-between items-center"
                onClick={openSheet}
              >
                <span className="overflow-hidden line-clamp-1 pr-2 w-full">
                  {displayValue}
                </span>
                <ChevronIcon
                  strokeWidth="1"
                  className="h-5 w-5 !stroke-1 text-white transition-transform duration-200 rotate-90 flex-shrink-0"
                />
              </div>

              <Sheet
                rootId="root"
                detent="content-height"
                isOpen={isSheetOpen}
                onClose={closeSheet}
                onMouseDown={(e) => e.stopPropagation()}
                tweenConfig={{ ease: [0.16, 1, 0.3, 1], duration: 0.55 }}
              >
                <Sheet.Container className="!bg-gray-4 rounded-t-2xl">
                  <Sheet.Header className="!bg-gray-4 border-none rounded-t-2xl" />
                  <Sheet.Content className="bg-gray-4 border-gray-4 pb-safe-bottom max-h-[82dvh]">
                    <div className="flex items-center px-4 justify-between gap-3 pb-3">
                      <div className="flex items-center gap-2">
                        {selectionMode === "district" && (
                          <VibratingButton
                            type="button"
                            onClick={() => setSelectionMode("region")}
                          >
                            <ChevronIcon
                              strokeWidth="1"
                              className="text-white rotate-180 w-6 h-6 cursor-pointer"
                            />
                          </VibratingButton>
                        )}
                        <h2 className="text-lg leading-130 font-bold text-white">
                          {selectionMode === "region"
                            ? t("select_region")
                            : t("select_district")}
                        </h2>
                      </div>
                      <VibratingButton type="button" onClick={closeSheet}>
                        <IconClose className="text-gray-1 w-6 h-6 cursor-pointer" />
                      </VibratingButton>
                    </div>

                    <Sheet.Scroller draggableAt="top" style={{ overscrollBehavior: "contain" }}>
                      {selectionMode === "region" ? (
                        isLoadingRegions ? (
                          <div className="px-4">
                            {[...Array(10)].map((_, index) => (
                              <div key={index}>
                                <div className="flex items-center px-0 py-[14px]">
                                  <span className="skeleton h-4 w-3/4 rounded-md"></span>
                                  <div className="skeleton h-6 w-6 rounded-md ml-auto"></div>
                                </div>
                                <p className="h-px w-full bg-white/08"></p>
                              </div>
                            ))}
                          </div>
                        ) : regions.length > 0 ? (
                          regions.map((region) => (
                            <div key={region.id}>
                              <div
                                className="flex items-center px-4 border-white/08 pb-[13px] pt-[15px] cursor-pointer"
                                onClick={() => handleSelectRegion(region.id)}
                              >
                                <span className="text-white font-semibold leading-130 text-sm overflow-hidden text-ellipsis whitespace-nowrap flex-1 pr-2">
                                  {region.name}
                                </span>
                                <ChevronIcon
                                  strokeWidth="1"
                                  className="h-6 w-6 aspect-square text-white ml-auto flex-shrink-0"
                                />
                              </div>
                              <p className="h-px w-full ml-4 bg-white/08"></p>
                            </div>
                          ))
                        ) : (
                          <div className="p-2.5 text-white text-sm">
                            {t("no_regions_found")}
                          </div>
                        )
                      ) : isLoadingDistricts ? (
                        <div className="px-4">
                          {[...Array(10)].map((_, index) => (
                            <div key={index}>
                              <div className="flex items-center px-0 py-[14px] justify-between">
                                <span className="skeleton h-4 w-3/4 rounded-md"></span>
                                <div className="skeleton h-6 w-6 rounded-full flex-shrink-0"></div>
                              </div>
                              <p className="h-px w-full ml-4 bg-white/08"></p>
                            </div>
                          ))}
                        </div>
                      ) : districts.length > 0 ? (
                        districts.map((district) => (
                          <div key={district.id}>
                            <div
                              className="p-2.5 justify-between border-white/08 flex items-center px-4 pb-[13px] pt-[15px] cursor-pointer"
                              onClick={() => handleSelectDistrict(district.id)}
                            >
                              <span className="text-white overflow-hidden text-ellipsis whitespace-nowrap flex-1 pr-2">
                                {district.name}
                              </span>
                              <div className="flex-shrink-0">
                                <input
                                  className="w-0 h-0 peer hidden"
                                  type="radio"
                                  checked={tempDistrict === district.id}
                                  readOnly
                                />
                                <div className="w-6 h-6 rounded-full border border-white/08 peer-checked:bg-blue peer-checked:border-blue transition-200 relative after:absolute after:left-1/2 after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:w-3 after:h-3 after:rounded-full after:bg-white after:duration-200 after:opacity-0 peer-checked:after:opacity-100 peer-checked:after:drop-shadow-[0_0_10px_#00224B]" />
                              </div>
                            </div>
                            <p className="h-px w-full ml-4 bg-white/08"></p>
                          </div>
                        ))
                      ) : (
                        <div className="p-2.5 text-white text-sm">
                          {t("no_districts_found")}
                        </div>
                      )}
                    </Sheet.Scroller>

                    {/* Only show confirmation button in district mode */}
                    {selectionMode === "district" && (
                      <div className="px-4 border-t border-white/08">
                        <BaseButton
                          text={t("confirmation")}
                          className="w-full mb-4 mt-4 border border-transparent"
                          onClick={handleConfirmDistrict}
                          disabled={isSubmitting}
                          isLoading={isSubmitting}
                        />
                      </div>
                    )}
                  </Sheet.Content>
                </Sheet.Container>
                <Sheet.Backdrop
                  className="!bg-[#0E0E14]/80"
                  onTap={closeSheet}
                />
              </Sheet>
            </div>
          </FormControl>
          {error && <FormMessage>{error}</FormMessage>}
        </FormItem>
      )}
    />
  );
};

export default RegionDistrictSelect;
